import { WIDGET_DEPENDENCIES } from './fieldProps'

/**
 * Build the standardized props object for a migrated widget.
 * Returns EXACTLY the keys from STANDARD_FIELD_PROPS — nothing more.
 *
 * @param {Object} params
 * @param {Object} params.data - The data wrapper object: { value, url?, name? }
 * @param {Object} params.fieldMeta - Merged model+view metadata for this field
 * @param {Object|undefined} params.widget - Resolved widget config: { type, arguments? }
 * @param {Object|null} params.record - Full record object (for cross-field deps)
 * @param {string} params.fieldId - Computed field identifier
 * @param {boolean} params.editable - Whether field is editable
 * @param {Array} params.bypassValidation - Rule names to filter out
 * @param {string|undefined} params.validationScope - Vuelidate $scope
 * @param {Array} params.callerValidationRules - Validation rules from caller ($attrs)
 * @param {string|undefined} params.errorMessage - External error message
 * @param {boolean|undefined} params.copyable - Whether field value is copyable
 * @param {string|undefined} params.linkBehavior - Link rendering mode: undefined (widget default), 'blank' (new tab), 'none' (no links)
 * @param {boolean} params.isElnLayout - Whether ELN layout mode is active
 * @returns {Object} - Props matching STANDARD_FIELD_PROPS keys exactly
 */
export function buildStandardProps({
  data,
  fieldMeta,
  widget,
  record,
  fieldId,
  editable,
  bypassValidation = [],
  validationScope,
  callerValidationRules = [],
  errorMessage,
  hasError = false,
  copyable,
  linkBehavior,
  isElnLayout = false,
  appName = '',
  page = '',
  widgetDefaultArgs = {},
}) {
  // --- value ---
  // Mirrors FieldComponent attrs() L278-306 value extraction logic.
  // Each branch handles a specific data shape; reordering or removing
  // a branch will break the widgets that depend on it.
  let value
  if (!data) {
    // No data wrapper at all (field absent from record).
    // Leave undefined — FieldResolver will show empty state.
    value = undefined
  } else if (fieldMeta.type === 'string' && fieldMeta.multiple) {
    // Multi-value string fields (e.g. tags): server sends an array of strings,
    // but string widgets expect a single comma-separated string for display.
    value = data.value ? data.value.join(', ') : ''
  } else if (editable && fieldMeta.choices) {
    // Editable choice fields (dropdowns): pass the raw value so the select
    // widget can match it against the choices list. Using data.value directly
    // preserves the original type (string, number, object) the server sent.
    value = data?.value
  } else {
    // Default: unwrap data.value. Covers objects, numbers, dates, plain strings.
    // Falls back to {} (not undefined) so view widgets always get a defined value.
    value = data ? data.value : {}
  }
  // Standalone string-type guard: when data is completely absent (undefined),
  // string widgets need '' not undefined to avoid rendering "undefined" as text.
  if (data === undefined && fieldMeta.type === 'string') {
    value = ''
  }

  // TODO-SERVER-011: Duration data has non-standard shape {created, completed}
  // instead of {value: {created, completed}}. Pass raw data as value.
  if (widget?.type === 'Duration') {
    value = data || {}
  }

  // --- uiArgs: UI-decided overrides (internal only, not exposed to widgets) ---
  // Each entry is a workaround for missing server metadata.
  // When the server provides a value in widget.arguments, delete the line here —
  // the server value will flow through serverArgs automatically.
  const uiArgs = {}

  // TODO-SERVER-013: label_field/value_field should always come from widget.arguments
  if (
    (widget?.type === 'Object' ||
      (!widget?.type && fieldMeta?.type === 'object')) &&
    fieldMeta?.choices
  ) {
    uiArgs.label_field = fieldMeta.choices.label_field
    uiArgs.value_field = fieldMeta.choices.value_field
  }

  // TODO-SERVER-014: showDeleted should come from server widget.arguments
  if (fieldMeta.name === 'studies') {
    uiArgs.showDeleted = false
  }

  // ItemLink: show item count and truncate long lists
  if (widget?.type === 'ItemLink' || widget?.type === 'LinkedItem') {
    uiArgs.showTotalCount = true
    uiArgs.maxItems = 10
  }

  // Caller-injected context — these are passed by views/listings on <FieldComponent>
  // and forwarded to widgets via widgetArgs. Ideally each would come from server
  // metadata (widget.arguments), but callers currently configure them at render time.
  if (copyable !== undefined) uiArgs.copyable = copyable
  if (linkBehavior) uiArgs.linkBehavior = linkBehavior
  if (isElnLayout) uiArgs.isElnLayout = isElnLayout
  if (appName) uiArgs.appName = appName
  if (page) uiArgs.page = page

  // --- widgetArgs: registration defaults → UI overrides → server wins ---
  const widgetArgs = {
    ...widgetDefaultArgs,
    ...uiArgs,
    ...(widget?.arguments || {}),
  }

  // --- validationRules ---
  // Mirrors FieldComponent L484-498
  let validationRules = [
    ...callerValidationRules,
    ...(fieldMeta.validations || []),
  ]
  if (bypassValidation.length > 0) {
    validationRules = validationRules.filter(
      ({ name }) => !bypassValidation.includes(name),
    )
  }
  // TODO-SERVER-009: name maxLength should come from server validations
  if (fieldMeta.name === 'name') {
    validationRules = [
      ...validationRules,
      { name: 'maxLength', arguments: [255] },
    ]
  }

  // --- dependencies ---
  // Extract only declared cross-field data from record
  const dependencies = {}
  const widgetType = widget?.type
  const depFields = WIDGET_DEPENDENCIES[widgetType]
  if (depFields && record) {
    for (const fieldName of depFields) {
      if (record[fieldName] !== undefined) {
        dependencies[fieldName] = record[fieldName]
      }
    }
  }

  return {
    value,
    fieldMeta,
    widgetArgs,
    fieldId,
    editable,
    validationRules,
    validationScope,
    errorMessage,
    hasError: hasError || !!errorMessage,
    dependencies,
  }
}
